import {
  LayoutDashboard,
  Users,
  BookOpen,
  DollarSign,
  MessageSquare,
  Settings,
  GraduationCap,
  Library,
  Home,
  Bus,
  Heart,
  BookMarked,
  Clock,
  Shield,
  Award,
  Briefcase,
  Building,
  CreditCard,
  MessageCircle,
  Gift,
  Users2,
  CalendarDays,
  AlertTriangle,
  Shirt,
  BarChart3,
  UserCheck,
  Bell,
  ArrowRightLeft,
  ChefHat,
  Utensils,
  Car,
  Stethoscope,
  Dumbbell,
  Package,
  UserCog,
  FileText,
  TrendingUp,
  ClipboardCheck,
  ClipboardList,
  Truck,
  Wallet,
  Scale,
} from "lucide-react";
import type { FinancialRouteGroup } from "./financialRoutes";
export interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  roles: string[];
  permissions?: string[];
  children?: NavigationItem[];
  priority?: number;
  category?: string;
  description?: string;
  // Sidebar sub-heading for a child whose page lives outside /financial (and
  // so has no group in financialRoutes.ts), e.g. Scholarships.
  group?: FinancialRouteGroup;
}

export interface RoleNavigationConfig {
  [role: string]: {
    primary: NavigationItem[];
    secondary: NavigationItem[];
    tertiary: NavigationItem[];
    quickActions?: NavigationItem[];
  };
}

// Define navigation items with categories and priorities
const navigationItems: NavigationItem[] = [
  // Core Dashboard - Always first
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    roles: ["*"], // Available to all roles
    priority: 1,
    category: "core",
  },

  // Academic Management
  {
    name: "Students",
    href: "/students",
    icon: Users,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 2,
    category: "academic",
  },
  {
    name: "Attendance",
    href: "/attendance",
    icon: UserCheck,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 3,
    category: "academic",
  },
  {
    name: "Admissions",
    href: "/admissions",
    icon: ClipboardList,
    roles: ["admin", "director", "head_teacher", "director_of_studies"],
    priority: 3,
    category: "academic",
  },
  {
    name: "Audit Log",
    href: "/audit-log",
    icon: ClipboardList,
    // The roles holding financial.view_audit_logs by default; the route checks the capability itself.
    roles: ["admin", "director", "principal", "cfo"],
    priority: 30,
    category: "administrative",
  },
  {
    name: "Academics",
    href: "/academics",
    icon: BookOpen,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 4,
    category: "academic",
  },
  {
    name: "Timetable",
    href: "/timetable",
    icon: Clock,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 5,
    category: "academic",
  },
  {
    name: "Examinations",
    href: "/examinations",
    icon: BookMarked,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 6,
    category: "academic",
  },

  // Financial Management
  {
    name: "Financial",
    href: "/financial",
    icon: DollarSign,
    roles: ["admin", "director", "head_teacher", "bursar"],
    priority: 7,
    category: "financial",
    children: [
      {
        name: "Revenue & Collections",
        href: "/financial/students",
        icon: Users,
        roles: ["admin", "director", "head_teacher", "bursar"],
        priority: 1,
        category: "financial",
        children: [
          { name: "Students", href: "/financial/students", icon: Users, roles: ["admin", "director", "head_teacher", "bursar"], priority: 1, category: "financial" },
          { name: "Fees", href: "/financial/fees", icon: DollarSign, roles: ["admin", "director", "head_teacher", "bursar"], priority: 2, category: "financial" },
          { name: "Fee Structures", href: "/financial/fee-structures", icon: ClipboardList, roles: ["admin", "director", "head_teacher", "bursar"], priority: 3, category: "financial" },
          { name: "Payments", href: "/financial/payments", icon: CreditCard, roles: ["admin", "director", "head_teacher", "bursar"], priority: 4, category: "financial" },
          { name: "Refunds", href: "/financial/refunds", icon: ArrowRightLeft, roles: ["admin", "director", "head_teacher", "bursar"], priority: 5, category: "financial" },
          { name: "Invoices", href: "/financial/invoices", icon: FileText, roles: ["admin", "director", "head_teacher", "bursar"], priority: 14, category: "financial" },
          { name: "Credit Notes", href: "/financial/credit-notes", icon: FileText, roles: ["admin", "director", "head_teacher", "bursar"], priority: 15, category: "financial" },
        ],
      },
      {
        name: "Service Charges",
        href: "/financial/transport",
        icon: Bus,
        roles: ["admin", "director", "head_teacher", "bursar"],
        priority: 2,
        category: "financial",
        children: [
          { name: "Transport Fees", href: "/financial/transport", icon: Bus, roles: ["admin", "director", "head_teacher", "bursar"], priority: 6, category: "financial" },
          { name: "Boarding Fees", href: "/financial/boarding", icon: Home, roles: ["admin", "director", "head_teacher", "bursar"], priority: 7, category: "financial" },
          { name: "Cafeteria Orders", href: "/financial/cafeteria", icon: Utensils, roles: ["admin", "director", "head_teacher", "bursar"], priority: 12, category: "financial" },
          { name: "Uniform Orders", href: "/financial/uniforms", icon: Shirt, roles: ["admin", "director", "head_teacher", "bursar"], priority: 13, category: "financial" },
        ],
      },
      {
        name: "Accounting",
        href: "/financial/accounts",
        icon: BookOpen,
        roles: ["admin", "director", "head_teacher", "bursar"],
        priority: 3,
        category: "financial",
        children: [
          { name: "Chart of Accounts", href: "/financial/accounts", icon: BookOpen, roles: ["admin", "director", "head_teacher", "bursar"], priority: 16, category: "financial" },
          { name: "Journals", href: "/financial/journals", icon: BookOpen, roles: ["admin", "director", "head_teacher", "bursar"], priority: 8, category: "financial" },
          { name: "Periods", href: "/financial/periods", icon: CalendarDays, roles: ["admin", "director", "head_teacher", "bursar"], priority: 17, category: "financial" },
          { name: "Payables", href: "/financial/payables", icon: Truck, roles: ["admin", "director", "head_teacher", "bursar"], priority: 9, category: "financial" },
          { name: "Banking", href: "/financial/banking", icon: Building, roles: ["admin", "director", "head_teacher", "bursar"], priority: 18, category: "financial" },
        ],
      },
      {
        name: "Planning & Reports",
        href: "/financial/budgets",
        icon: TrendingUp,
        roles: ["admin", "director", "head_teacher", "bursar"],
        priority: 4,
        category: "financial",
        children: [
          { name: "Budgets", href: "/financial/budgets", icon: FileText, roles: ["admin", "director", "head_teacher", "bursar"], priority: 10, category: "financial" },
          { name: "Grants", href: "/financial/grants", icon: Gift, roles: ["admin", "director", "head_teacher", "bursar"], priority: 19, category: "financial" },
          { name: "Payroll", href: "/financial/payroll", icon: Wallet, roles: ["admin", "director", "head_teacher", "bursar"], priority: 11, category: "financial" },
          { name: "Trial Balance", href: "/financial/trial-balance", icon: Scale, roles: ["admin", "director", "head_teacher", "bursar"], priority: 20, category: "financial" },
          { name: "Account Statement", href: "/financial/account-statement", icon: BookOpen, roles: ["admin", "director", "head_teacher", "bursar"], priority: 20, category: "financial" },
          { name: "Financial Statements", href: "/financial/financial-statements", icon: FileText, roles: ["admin", "director", "head_teacher", "bursar"], priority: 20, category: "financial" },
          { name: "Management Reports", href: "/financial/statements", icon: TrendingUp, roles: ["admin", "director", "head_teacher", "bursar"], priority: 20, category: "financial" },
        ],
      },
    ],
  },
  {
    name: "Governance Oversight",
    href: "/governance",
    icon: BarChart3,
    roles: ["admin", "director", "board_viewer", "donor_viewer"],
    priority: 8,
    category: "financial",
  },

  // Support Services
  {
    name: "Library",
    href: "/library",
    icon: Library,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "librarian",
      "teacher",
      "student",
    ],
    priority: 9,
    category: "support",
  },
  {
    name: "Medical",
    href: "/medical",
    icon: Heart,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "school_doctor",
      "class_teacher",
      "parent",
    ],
    priority: 10,
    category: "support",
  },
  {
    name: "Transport",
    href: "/transport",
    icon: Bus,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "transport_manager",
      "bus_driver",
      "parent",
    ],
    priority: 11,
    category: "support",
  },
  {
    name: "Hostel",
    href: "/hostel",
    icon: Home,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "hostel_matron",
      "hostel_custodian",
    ],
    priority: 12,
    category: "support",
  },
  {
    name: "Sports",
    href: "/sports",
    icon: Award,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "sports_master",
      "teacher",
      "student",
    ],
    priority: 13,
    category: "support",
  },

  // Communication & Events
  {
    name: "Communication",
    href: "/communication",
    icon: MessageSquare,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
      "parent",
    ],
    priority: 14,
    category: "communication",
  },
  {
    name: "Events",
    href: "/events",
    icon: CalendarDays,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
      "parent",
      "student",
    ],
    priority: 15,
    category: "communication",
  },
  {
    name: "Notifications",
    href: "/notifications",
    icon: Bell,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
      "parent",
    ],
    priority: 16,
    category: "communication",
  },

  // Administrative
  {
    name: "Staff",
    href: "/staff",
    icon: Briefcase,
    roles: ["admin", "director", "head_teacher", "director_of_studies"],
    priority: 17,
    category: "administrative",
  },
  {
    name: "Inventory",
    href: "/inventory",
    icon: Building,
    roles: ["admin", "director", "head_teacher", "inventory_manager"],
    priority: 18,
    category: "administrative",
  },
  {
    name: "Analytics",
    href: "/analytics",
    icon: BarChart3,
    roles: ["admin", "director", "head_teacher", "director_of_studies"],
    priority: 19,
    category: "administrative",
  },

  // Discipline & Counseling
  {
    name: "Disciplinary",
    href: "/disciplinary",
    icon: Shield,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
      "disciplinary_committee",
    ],
    priority: 20,
    category: "discipline",
  },
  {
    name: "Counseling",
    href: "/counseling",
    icon: MessageCircle,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "school_counsellor",
      "class_teacher",
      "teacher",
    ],
    priority: 21,
    category: "discipline",
  },

  // Student-specific
  {
    name: "My Progress",
    href: "/student/gamification",
    icon: Award,
    roles: ["student"],
    priority: 2,
    category: "student",
  },
  {
    name: "Parent Portal",
    href: "/portal",
    icon: Users,
    roles: ["parent", "student"],
    priority: 2,
    category: "portal",
  },

  // Role-specific dashboards (only show primary dashboard for each role)
  {
    name: "Class Teacher Dashboard",
    href: "/class-teacher/dashboard",
    icon: Users2,
    roles: ["class_teacher"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Teacher Dashboard",
    href: "/teacher/dashboard",
    icon: BookOpen,
    roles: ["teacher", "head_of_department"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Director of Studies Dashboard",
    href: "/director-of-studies/dashboard",
    icon: GraduationCap,
    roles: ["director_of_studies"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "School Doctor Dashboard",
    href: "/doctor/dashboard",
    icon: Stethoscope,
    roles: ["school_doctor"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Bursar Dashboard",
    href: "/bursar/dashboard",
    icon: DollarSign,
    roles: ["bursar"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Headmaster Dashboard",
    href: "/headmaster/dashboard",
    icon: GraduationCap,
    roles: ["head_teacher", "director"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Librarian Dashboard",
    href: "/librarian/dashboard",
    icon: Library,
    roles: ["librarian"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Transport Manager Dashboard",
    href: "/transport-manager/dashboard",
    icon: Car,
    roles: ["transport_manager"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Bus Driver Dashboard",
    href: "/bus-driver/dashboard",
    icon: Bus,
    roles: ["bus_driver"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Hostel Matron Dashboard",
    href: "/hostel-matron/dashboard",
    icon: Home,
    roles: ["hostel_matron"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Hostel Custodian Dashboard",
    href: "/hostel-custodian/dashboard",
    icon: Home,
    roles: ["hostel_custodian"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Kitchen Manager Dashboard",
    href: "/kitchen-manager/dashboard",
    icon: ChefHat,
    roles: ["kitchen_manager"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Cafeteria Manager Dashboard",
    href: "/cafeteria-manager/dashboard",
    icon: Utensils,
    roles: ["cafeteria_manager"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Sports Master Dashboard",
    href: "/sports-master/dashboard",
    icon: Dumbbell,
    roles: ["sports_master"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Uniform Manager Dashboard",
    href: "/uniform-manager/dashboard",
    icon: Shirt,
    roles: ["uniform_manager"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Inventory Manager Dashboard",
    href: "/inventory-manager/dashboard",
    icon: Package,
    roles: ["inventory_manager"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "School Counsellor Dashboard",
    href: "/counsellor/dashboard",
    icon: UserCog,
    roles: ["school_counsellor"],
    priority: 2,
    category: "dashboard",
  },
  {
    name: "Disciplinary Committee Dashboard",
    href: "/disciplinary-committee/dashboard",
    icon: Shield,
    roles: ["disciplinary_committee"],
    priority: 2,
    category: "dashboard",
  },

  // Additional services (lower priority)
  {
    name: "Cafeteria",
    href: "/cafeteria",
    icon: Utensils,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "kitchen_manager",
      "cafeteria_manager",
      "parent",
      "student",
    ],
    priority: 30,
    category: "services",
  },
  {
    name: "Scholarships",
    href: "/scholarships",
    icon: Gift,
    roles: ["admin", "director", "head_teacher", "bursar", "parent"],
    priority: 31,
    category: "services",
  },
  {
    name: "Meetings",
    href: "/meetings",
    icon: Users2,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 32,
    category: "services",
  },
  {
    name: "Emergency",
    href: "/emergency",
    icon: AlertTriangle,
    roles: [
      "admin",
      "director",
      "head_teacher",
      "director_of_studies",
      "head_of_department",
      "class_teacher",
      "teacher",
    ],
    priority: 33,
    category: "services",
  },
  {
    name: "Uniforms",
    href: "/uniforms",
    icon: Shirt,
    roles: ["admin", "director", "head_teacher", "uniform_manager"],
    priority: 34,
    category: "services",
  },
  {
    name: "Alumni",
    href: "/alumni",
    icon: GraduationCap,
    roles: ["admin", "director", "head_teacher"],
    priority: 35,
    category: "services",
  },
  {
    name: "Student Lifecycle",
    href: "/student-lifecycle",
    icon: ArrowRightLeft,
    roles: ["admin", "director", "head_teacher", "director_of_studies"],
    priority: 36,
    category: "services",
  },
  {
    name: "People & Access",
    href: "/administration",
    icon: Settings,
    roles: ["admin", "director", "head_teacher"],
    priority: 40,
    category: "administrative",
  },
];

// Role-specific navigation configurations
export const roleBasedNavigation: RoleNavigationConfig = {
  // Admin - Full access to everything
  admin: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["admin"],
        priority: 1,
      },
      {
        name: "People & Access",
        href: "/administration",
        icon: UserCog,
        roles: ["admin"],
        priority: 2,
      },
      {
        name: "Students",
        href: "/students",
        icon: Users,
        roles: ["admin"],
        priority: 3,
      },
      {
        name: "Staff",
        href: "/staff",
        icon: Briefcase,
        roles: ["admin"],
        priority: 4,
      },
      {
        name: "Financial",
        href: "/financial",
        icon: DollarSign,
        roles: ["admin"],
        priority: 5,
        children: [
          { name: "Students", href: "/financial/students", icon: Users, roles: ["admin"], priority: 1, category: "financial" },
          { name: "Fees", href: "/financial/fees", icon: DollarSign, roles: ["admin"], priority: 2, category: "financial" },
          { name: "Fee Structures", href: "/financial/fee-structures", icon: ClipboardList, roles: ["admin"], priority: 3, category: "financial" },
          { name: "Payments", href: "/financial/payments", icon: CreditCard, roles: ["admin"], priority: 4, category: "financial" },
          { name: "Refunds", href: "/financial/refunds", icon: ArrowRightLeft, roles: ["admin"], priority: 5, category: "financial" },
          { name: "Transport Fees", href: "/financial/transport", icon: Bus, roles: ["admin"], priority: 6, category: "financial" },
          { name: "Boarding Fees", href: "/financial/boarding", icon: Home, roles: ["admin"], priority: 7, category: "financial" },
          { name: "Journals", href: "/financial/journals", icon: BookOpen, roles: ["admin"], priority: 8, category: "financial" },
          { name: "Payables", href: "/financial/payables", icon: Truck, roles: ["admin"], priority: 9, category: "financial" },
          { name: "Budgets", href: "/financial/budgets", icon: FileText, roles: ["admin"], priority: 10, category: "financial" },
          { name: "Payroll", href: "/financial/payroll", icon: Wallet, roles: ["admin"], priority: 11, category: "financial" },
          { name: "Cafeteria Orders", href: "/financial/cafeteria", icon: Utensils, roles: ["admin"], priority: 12, category: "financial" },
          { name: "Uniform Orders", href: "/financial/uniforms", icon: Shirt, roles: ["admin"], priority: 13, category: "financial" },
          { name: "Invoices", href: "/financial/invoices", icon: FileText, roles: ["admin"], priority: 14, category: "financial" },
          { name: "Credit Notes", href: "/financial/credit-notes", icon: FileText, roles: ["admin"], priority: 15, category: "financial" },
          { name: "Chart of Accounts", href: "/financial/accounts", icon: BookOpen, roles: ["admin"], priority: 16, category: "financial" },
          { name: "Periods", href: "/financial/periods", icon: CalendarDays, roles: ["admin"], priority: 17, category: "financial" },
          { name: "Banking", href: "/financial/banking", icon: Building, roles: ["admin"], priority: 18, category: "financial" },
          { name: "Grants", href: "/financial/grants", icon: Gift, roles: ["admin"], priority: 19, category: "financial" },
          { name: "Trial Balance", href: "/financial/trial-balance", icon: Scale, roles: ["admin"], priority: 20, category: "financial" },
          { name: "Account Statement", href: "/financial/account-statement", icon: BookOpen, roles: ["admin"], priority: 20, category: "financial" },
          { name: "Financial Statements", href: "/financial/financial-statements", icon: FileText, roles: ["admin"], priority: 20, category: "financial" },
          { name: "Management Reports", href: "/financial/statements", icon: TrendingUp, roles: ["admin"], priority: 20, category: "financial" },
        ],
      },
      {
        name: "My Approvals",
        href: "/my-approvals",
        icon: ClipboardList,
        roles: ["admin"],
        priority: 5.5,
      },
      {
        name: "Permissions",
        href: "/permissions",
        icon: Shield,
        roles: ["admin"],
        priority: 5.7,
      },
      {
        name: "Analytics",
        href: "/analytics",
        icon: BarChart3,
        roles: ["admin"],
        priority: 6,
      },
    ],
    secondary: [
      {
        name: "Admissions",
        href: "/admissions",
        icon: UserCheck,
        roles: ["admin"],
        priority: 20,
      },
      {
        name: "Attendance",
        href: "/attendance",
        icon: Clock,
        roles: ["admin"],
        priority: 21,
      },
      {
        name: "Examinations",
        href: "/examinations",
        icon: Award,
        roles: ["admin"],
        priority: 22,
      },
      {
        name: "Curriculum",
        href: "/curriculum",
        icon: BookMarked,
        roles: ["admin"],
        priority: 23,
      },
      {
        name: "Class Setup",
        href: "/class-setup",
        icon: ClipboardList,
        roles: ["admin"],
        priority: 24,
      },
      {
        name: "Student Lifecycle",
        href: "/student-lifecycle",
        icon: Users2,
        roles: ["admin"],
        priority: 25,
      },
      {
        name: "Scholarships",
        href: "/scholarships",
        icon: Gift,
        roles: ["admin"],
        priority: 26,
      },
      {
        name: "Parents & Guardians",
        href: "/parents",
        icon: Users,
        roles: ["admin"],
        priority: 27,
      },
      {
        name: "Alumni",
        href: "/alumni",
        icon: GraduationCap,
        roles: ["admin"],
        priority: 28,
      },
      {
        name: "Counseling",
        href: "/counseling",
        icon: Heart,
        roles: ["admin"],
        priority: 29,
      },
      {
        name: "Cafeteria",
        href: "/cafeteria",
        icon: Utensils,
        roles: ["admin"],
        priority: 30,
      },
      {
        name: "Uniforms",
        href: "/uniforms",
        icon: Shirt,
        roles: ["admin"],
        priority: 31,
      },
      {
        name: "Facilities",
        href: "/facilities",
        icon: Building,
        roles: ["admin"],
        priority: 32,
      },
      {
        name: "Meetings",
        href: "/meetings",
        icon: CalendarDays,
        roles: ["admin"],
        priority: 33,
      },
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["admin"],
        priority: 34,
      },
      {
        name: "Governance",
        href: "/governance",
        icon: Scale,
        roles: ["admin"],
        priority: 35,
      },
      {
        name: "Audit Log",
        href: "/audit-log",
        icon: Shield,
        roles: ["admin"],
        priority: 36,
      },
      {
        name: "HR Setup",
        href: "/hr-setup",
        icon: UserCog,
        roles: ["admin"],
        priority: 6,
      },
      {
        name: "Academics",
        href: "/academics",
        icon: BookOpen,
        roles: ["admin"],
        priority: 7,
      },
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["admin"],
        priority: 7,
      },
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["admin"],
        priority: 8,
      },
      {
        name: "Medical",
        href: "/medical",
        icon: Heart,
        roles: ["admin"],
        priority: 9,
      },
      {
        name: "Inventory",
        href: "/inventory",
        icon: Package,
        roles: ["admin"],
        priority: 11,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["admin"],
        priority: 10,
      },
    ],
    tertiary: [
      {
        name: "Headmaster Dashboard",
        href: "/headmaster/dashboard",
        icon: LayoutDashboard,
        roles: ["admin"],
        priority: 60,
      },
      {
        name: "Director of Studies Dashboard",
        href: "/director-of-studies/dashboard",
        icon: LayoutDashboard,
        roles: ["admin"],
        priority: 61,
      },
      {
        name: "Bursar Dashboard",
        href: "/bursar/dashboard",
        icon: Wallet,
        roles: ["admin"],
        priority: 62,
      },
      {
        name: "Librarian Dashboard",
        href: "/librarian/dashboard",
        icon: Library,
        roles: ["admin"],
        priority: 63,
      },
      {
        name: "Doctor Dashboard",
        href: "/doctor/dashboard",
        icon: Stethoscope,
        roles: ["admin"],
        priority: 64,
      },
      {
        name: "Counsellor Dashboard",
        href: "/counsellor/dashboard",
        icon: Heart,
        roles: ["admin"],
        priority: 65,
      },
      {
        name: "Transport Manager Dashboard",
        href: "/transport-manager/dashboard",
        icon: Bus,
        roles: ["admin"],
        priority: 66,
      },
      {
        name: "Bus Driver Dashboard",
        href: "/bus-driver/dashboard",
        icon: Car,
        roles: ["admin"],
        priority: 67,
      },
      {
        name: "Hostel Matron Dashboard",
        href: "/hostel-matron/dashboard",
        icon: Home,
        roles: ["admin"],
        priority: 68,
      },
      {
        name: "Hostel Custodian Dashboard",
        href: "/hostel-custodian/dashboard",
        icon: Home,
        roles: ["admin"],
        priority: 69,
      },
      {
        name: "Kitchen Manager Dashboard",
        href: "/kitchen-manager/dashboard",
        icon: ChefHat,
        roles: ["admin"],
        priority: 70,
      },
      {
        name: "Cafeteria Manager Dashboard",
        href: "/cafeteria-manager/dashboard",
        icon: Utensils,
        roles: ["admin"],
        priority: 71,
      },
      {
        name: "Sports Master Dashboard",
        href: "/sports-master/dashboard",
        icon: Dumbbell,
        roles: ["admin"],
        priority: 72,
      },
      {
        name: "Uniform Manager Dashboard",
        href: "/uniform-manager/dashboard",
        icon: Shirt,
        roles: ["admin"],
        priority: 73,
      },
      {
        name: "Inventory Manager Dashboard",
        href: "/inventory-manager/dashboard",
        icon: Package,
        roles: ["admin"],
        priority: 74,
      },
      {
        name: "Disciplinary Committee",
        href: "/disciplinary-committee/dashboard",
        icon: Shield,
        roles: ["admin"],
        priority: 75,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["admin"],
        priority: 11,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["admin"],
        priority: 12,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["admin"],
        priority: 13,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["admin"],
        priority: 14,
      },
      {
        name: "Disciplinary",
        href: "/disciplinary",
        icon: Shield,
        roles: ["admin"],
        priority: 15,
      },
    ],
  },

  // Director - High-level management
  director: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["director"],
        priority: 1,
      },
      {
        name: "People & Access",
        href: "/administration",
        icon: UserCog,
        roles: ["director"],
        priority: 2,
      },
      {
        name: "Students",
        href: "/students",
        icon: Users,
        roles: ["director"],
        priority: 3,
      },
      {
        name: "Staff",
        href: "/staff",
        icon: Briefcase,
        roles: ["director"],
        priority: 4,
      },
      {
        name: "Financial",
        href: "/financial",
        icon: DollarSign,
        roles: ["director"],
        priority: 5,
        children: [
          { name: "Students", href: "/financial/students", icon: Users, roles: ["director"], priority: 1, category: "financial" },
          { name: "Fees", href: "/financial/fees", icon: DollarSign, roles: ["director"], priority: 2, category: "financial" },
          { name: "Fee Structures", href: "/financial/fee-structures", icon: ClipboardList, roles: ["director"], priority: 3, category: "financial" },
          { name: "Payments", href: "/financial/payments", icon: CreditCard, roles: ["director"], priority: 4, category: "financial" },
          { name: "Refunds", href: "/financial/refunds", icon: ArrowRightLeft, roles: ["director"], priority: 5, category: "financial" },
          { name: "Transport Fees", href: "/financial/transport", icon: Bus, roles: ["director"], priority: 6, category: "financial" },
          { name: "Boarding Fees", href: "/financial/boarding", icon: Home, roles: ["director"], priority: 7, category: "financial" },
          { name: "Journals", href: "/financial/journals", icon: BookOpen, roles: ["director"], priority: 8, category: "financial" },
          { name: "Payables", href: "/financial/payables", icon: Truck, roles: ["director"], priority: 9, category: "financial" },
          { name: "Budgets", href: "/financial/budgets", icon: FileText, roles: ["director"], priority: 10, category: "financial" },
          { name: "Payroll", href: "/financial/payroll", icon: Wallet, roles: ["director"], priority: 11, category: "financial" },
          { name: "Cafeteria Orders", href: "/financial/cafeteria", icon: Utensils, roles: ["director"], priority: 12, category: "financial" },
          { name: "Uniform Orders", href: "/financial/uniforms", icon: Shirt, roles: ["director"], priority: 13, category: "financial" },
          { name: "Invoices", href: "/financial/invoices", icon: FileText, roles: ["director"], priority: 14, category: "financial" },
          { name: "Credit Notes", href: "/financial/credit-notes", icon: FileText, roles: ["director"], priority: 15, category: "financial" },
          { name: "Chart of Accounts", href: "/financial/accounts", icon: BookOpen, roles: ["director"], priority: 16, category: "financial" },
          { name: "Periods", href: "/financial/periods", icon: CalendarDays, roles: ["director"], priority: 17, category: "financial" },
          { name: "Banking", href: "/financial/banking", icon: Building, roles: ["director"], priority: 18, category: "financial" },
          { name: "Grants", href: "/financial/grants", icon: Gift, roles: ["director"], priority: 19, category: "financial" },
          { name: "Trial Balance", href: "/financial/trial-balance", icon: Scale, roles: ["director"], priority: 20, category: "financial" },
          { name: "Account Statement", href: "/financial/account-statement", icon: BookOpen, roles: ["director"], priority: 20, category: "financial" },
          { name: "Financial Statements", href: "/financial/financial-statements", icon: FileText, roles: ["director"], priority: 20, category: "financial" },
          { name: "Management Reports", href: "/financial/statements", icon: TrendingUp, roles: ["director"], priority: 20, category: "financial" },
        ],
      },
    ],
    secondary: [
      {
        name: "HR Setup",
        href: "/hr-setup",
        icon: UserCog,
        roles: ["director"],
        priority: 6,
      },
      {
        name: "Analytics",
        href: "/analytics",
        icon: BarChart3,
        roles: ["director"],
        priority: 7,
      },
      {
        name: "Academics",
        href: "/academics",
        icon: BookOpen,
        roles: ["director"],
        priority: 7,
      },
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["director"],
        priority: 8,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["director"],
        priority: 9,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["director"],
        priority: 10,
      },
    ],
    tertiary: [
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["director"],
        priority: 11,
      },
      {
        name: "Medical",
        href: "/medical",
        icon: Heart,
        roles: ["director"],
        priority: 12,
      },
      {
        name: "Inventory",
        href: "/inventory",
        icon: Package,
        roles: ["director"],
        priority: 11,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["director"],
        priority: 13,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["director"],
        priority: 14,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["director"],
        priority: 15,
      },
    ],
  },

  // Head Teacher - School management
  head_teacher: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["head_teacher"],
        priority: 1,
      },
      {
        name: "Headmaster Dashboard",
        href: "/headmaster/dashboard",
        icon: GraduationCap,
        roles: ["head_teacher"],
        priority: 2,
      },
      {
        name: "Students",
        href: "/students",
        icon: Users,
        roles: ["head_teacher"],
        priority: 3,
      },
    ],
    secondary: [
      {
        name: "Analytics",
        href: "/analytics",
        icon: BarChart3,
        roles: ["head_teacher"],
        priority: 7,
      },
      {
        name: "Academics",
        href: "/academics",
        icon: BookOpen,
        roles: ["head_teacher"],
        priority: 7,
      },
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["head_teacher"],
        priority: 8,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["head_teacher"],
        priority: 9,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["head_teacher"],
        priority: 10,
      },
    ],
    tertiary: [
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["head_teacher"],
        priority: 11,
      },
      {
        name: "Medical",
        href: "/medical",
        icon: Heart,
        roles: ["head_teacher"],
        priority: 12,
      },
      {
        name: "Inventory",
        href: "/inventory",
        icon: Package,
        roles: ["head_teacher"],
        priority: 11,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["head_teacher"],
        priority: 13,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["head_teacher"],
        priority: 14,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["head_teacher"],
        priority: 15,
      },
    ],
  },

  // Director of Studies - Academic management
  director_of_studies: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["director_of_studies"],
        priority: 1,
      },
      {
        name: "Director of Studies Dashboard",
        href: "/director-of-studies/dashboard",
        icon: GraduationCap,
        roles: ["director_of_studies"],
        priority: 2,
      },
      {
        name: "Students",
        href: "/students",
        icon: Users,
        roles: ["director_of_studies"],
        priority: 3,
      },
      {
        name: "Academics",
        href: "/academics",
        icon: BookOpen,
        roles: ["director_of_studies"],
        priority: 4,
      },
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["director_of_studies"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Staff",
        href: "/staff",
        icon: Briefcase,
        roles: ["director_of_studies"],
        priority: 6,
      },
      {
        name: "Analytics",
        href: "/analytics",
        icon: BarChart3,
        roles: ["director_of_studies"],
        priority: 7,
      },
      {
        name: "Examinations",
        href: "/examinations",
        icon: BookMarked,
        roles: ["director_of_studies"],
        priority: 8,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["director_of_studies"],
        priority: 9,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["director_of_studies"],
        priority: 10,
      },
    ],
    tertiary: [
      {
        name: "Student Lifecycle",
        href: "/student-lifecycle",
        icon: ArrowRightLeft,
        roles: ["director_of_studies"],
        priority: 11,
      },
      {
        name: "Disciplinary",
        href: "/disciplinary",
        icon: Shield,
        roles: ["director_of_studies"],
        priority: 12,
      },
      {
        name: "Counseling",
        href: "/counseling",
        icon: MessageCircle,
        roles: ["director_of_studies"],
        priority: 13,
      },
      {
        name: "Meetings",
        href: "/meetings",
        icon: Users2,
        roles: ["director_of_studies"],
        priority: 14,
      },
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["director_of_studies"],
        priority: 15,
      },
    ],
  },

  // Class Teacher - Student-focused
  class_teacher: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["class_teacher"],
        priority: 1,
      },
      {
        name: "Class Teacher Dashboard",
        href: "/class-teacher/dashboard",
        icon: Users2,
        roles: ["class_teacher"],
        priority: 2,
      },
      {
        name: "Students",
        href: "/students",
        icon: Users,
        roles: ["class_teacher"],
        priority: 3,
      },
      {
        name: "Attendance",
        href: "/attendance",
        icon: UserCheck,
        roles: ["class_teacher"],
        priority: 4,
      },
      {
        name: "Academics",
        href: "/academics",
        icon: BookOpen,
        roles: ["class_teacher"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["class_teacher"],
        priority: 6,
      },
      {
        name: "Examinations",
        href: "/examinations",
        icon: BookMarked,
        roles: ["class_teacher"],
        priority: 6,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["class_teacher"],
        priority: 7,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["class_teacher"],
        priority: 8,
      },
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["class_teacher"],
        priority: 9,
      },
    ],
    tertiary: [
      {
        name: "Disciplinary",
        href: "/disciplinary",
        icon: Shield,
        roles: ["class_teacher"],
        priority: 11,
      },
      {
        name: "Counseling",
        href: "/counseling",
        icon: MessageCircle,
        roles: ["class_teacher"],
        priority: 12,
      },
      {
        name: "Meetings",
        href: "/meetings",
        icon: Users2,
        roles: ["class_teacher"],
        priority: 13,
      },
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["class_teacher"],
        priority: 14,
      },
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["class_teacher"],
        priority: 15,
      },
    ],
  },

  // Teacher - Teaching-focused
  teacher: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["teacher"],
        priority: 1,
      },
      {
        name: "Teacher Dashboard",
        href: "/teacher/dashboard",
        icon: BookOpen,
        roles: ["teacher"],
        priority: 2,
      },
      {
        name: "Students",
        href: "/students",
        icon: Users,
        roles: ["teacher"],
        priority: 3,
      },
      {
        name: "Attendance",
        href: "/attendance",
        icon: UserCheck,
        roles: ["teacher"],
        priority: 4,
      },
      {
        name: "Academics",
        href: "/academics",
        icon: BookOpen,
        roles: ["teacher"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["teacher"],
        priority: 6,
      },
      {
        name: "Examinations",
        href: "/examinations",
        icon: BookMarked,
        roles: ["teacher"],
        priority: 6,
      },
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["teacher"],
        priority: 7,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["teacher"],
        priority: 8,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["teacher"],
        priority: 9,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["teacher"],
        priority: 10,
      },
    ],
    tertiary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["teacher"],
        priority: 11,
      },
      {
        name: "Meetings",
        href: "/meetings",
        icon: Users2,
        roles: ["teacher"],
        priority: 12,
      },
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["teacher"],
        priority: 13,
      },
      {
        name: "Disciplinary",
        href: "/disciplinary",
        icon: Shield,
        roles: ["teacher"],
        priority: 14,
      },
      {
        name: "Counseling",
        href: "/counseling",
        icon: MessageCircle,
        roles: ["teacher"],
        priority: 15,
      },
    ],
  },

  // Bursar - Financial management
  bursar: {
    primary: [
      // /dashboard renders the bursar dashboard for this role, so a separate
      // /bursar/dashboard link would open the same page twice.
      {
        name: "Bursar Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["bursar"],
        priority: 1,
      },
      {
        name: "Financial",
        href: "/financial",
        icon: DollarSign,
        roles: ["bursar"],
        priority: 3,
        children: [
          { name: "Students", href: "/financial/students", icon: Users, roles: ["bursar"], priority: 1, category: "financial" },
          { name: "Fees", href: "/financial/fees", icon: DollarSign, roles: ["bursar"], priority: 2, category: "financial" },
          { name: "Fee Structures", href: "/financial/fee-structures", icon: ClipboardList, roles: ["bursar"], priority: 3, category: "financial" },
          { name: "Payments", href: "/financial/payments", icon: CreditCard, roles: ["bursar"], priority: 4, category: "financial" },
          { name: "Refunds", href: "/financial/refunds", icon: ArrowRightLeft, roles: ["bursar"], priority: 5, category: "financial" },
          { name: "Transport Fees", href: "/financial/transport", icon: Bus, roles: ["bursar"], priority: 6, category: "financial" },
          { name: "Boarding Fees", href: "/financial/boarding", icon: Home, roles: ["bursar"], priority: 7, category: "financial" },
          { name: "Journals", href: "/financial/journals", icon: BookOpen, roles: ["bursar"], priority: 8, category: "financial" },
          { name: "Payables", href: "/financial/payables", icon: Truck, roles: ["bursar"], priority: 9, category: "financial" },
          { name: "Budgets", href: "/financial/budgets", icon: FileText, roles: ["bursar"], priority: 10, category: "financial" },
          { name: "Payroll", href: "/financial/payroll", icon: Wallet, roles: ["bursar"], priority: 11, category: "financial" },
          { name: "Cafeteria Orders", href: "/financial/cafeteria", icon: Utensils, roles: ["bursar"], priority: 12, category: "financial" },
          { name: "Uniform Orders", href: "/financial/uniforms", icon: Shirt, roles: ["bursar"], priority: 13, category: "financial" },
          { name: "Invoices", href: "/financial/invoices", icon: FileText, roles: ["bursar"], priority: 14, category: "financial" },
          { name: "Credit Notes", href: "/financial/credit-notes", icon: FileText, roles: ["bursar"], priority: 15, category: "financial" },
          { name: "Scholarships", href: "/scholarships", icon: Gift, roles: ["bursar"], priority: 16, category: "financial", group: "collections" },
          { name: "Chart of Accounts", href: "/financial/accounts", icon: BookOpen, roles: ["bursar"], priority: 16, category: "financial" },
          { name: "Periods", href: "/financial/periods", icon: CalendarDays, roles: ["bursar"], priority: 17, category: "financial" },
          { name: "Banking", href: "/financial/banking", icon: Building, roles: ["bursar"], priority: 18, category: "financial" },
          { name: "Grants", href: "/financial/grants", icon: Gift, roles: ["bursar"], priority: 19, category: "financial" },
          { name: "Trial Balance", href: "/financial/trial-balance", icon: Scale, roles: ["bursar"], priority: 20, category: "financial" },
          { name: "Account Statement", href: "/financial/account-statement", icon: BookOpen, roles: ["bursar"], priority: 20, category: "financial" },
          { name: "Financial Statements", href: "/financial/financial-statements", icon: FileText, roles: ["bursar"], priority: 20, category: "financial" },
          { name: "Management Reports", href: "/financial/statements", icon: TrendingUp, roles: ["bursar"], priority: 20, category: "financial" },
        ],
      },
    ],
    secondary: [
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["bursar"],
        priority: 7,
      },
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["bursar"],
        priority: 9,
      },
    ],
    tertiary: [
    ],
  },

  // Principal - reviews what the bursar prepares
  principal: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["principal"],
        priority: 1,
      },
      {
        name: "My Approvals",
        href: "/my-approvals",
        icon: ClipboardList,
        roles: ["principal"],
        priority: 2,
      },
      {
        // Salary changes are reviewed and approved here, so both halves of
        // the chain need a way in.
        name: "Staff",
        href: "/staff",
        icon: Users,
        roles: ["principal"],
        priority: 3,
      },
      {
        name: "Financial",
        href: "/financial",
        icon: DollarSign,
        roles: ["principal"],
        priority: 3,
        children: [
          { name: "Students", href: "/financial/students", icon: Users, roles: ["principal"], priority: 1, category: "financial" },
          { name: "Fees", href: "/financial/fees", icon: DollarSign, roles: ["principal"], priority: 2, category: "financial" },
          { name: "Fee Structures", href: "/financial/fee-structures", icon: ClipboardList, roles: ["principal"], priority: 3, category: "financial" },
          { name: "Payments", href: "/financial/payments", icon: CreditCard, roles: ["principal"], priority: 4, category: "financial" },
          { name: "Refunds", href: "/financial/refunds", icon: ArrowRightLeft, roles: ["principal"], priority: 5, category: "financial" },
          { name: "Transport Fees", href: "/financial/transport", icon: Bus, roles: ["principal"], priority: 6, category: "financial" },
          { name: "Boarding Fees", href: "/financial/boarding", icon: Home, roles: ["principal"], priority: 7, category: "financial" },
          { name: "Journals", href: "/financial/journals", icon: BookOpen, roles: ["principal"], priority: 8, category: "financial" },
          { name: "Payables", href: "/financial/payables", icon: Truck, roles: ["principal"], priority: 9, category: "financial" },
          { name: "Budgets", href: "/financial/budgets", icon: FileText, roles: ["principal"], priority: 10, category: "financial" },
          { name: "Payroll", href: "/financial/payroll", icon: Wallet, roles: ["principal"], priority: 11, category: "financial" },
          { name: "Cafeteria Orders", href: "/financial/cafeteria", icon: Utensils, roles: ["principal"], priority: 12, category: "financial" },
          { name: "Uniform Orders", href: "/financial/uniforms", icon: Shirt, roles: ["principal"], priority: 13, category: "financial" },
          { name: "Invoices", href: "/financial/invoices", icon: FileText, roles: ["principal"], priority: 14, category: "financial" },
          { name: "Credit Notes", href: "/financial/credit-notes", icon: FileText, roles: ["principal"], priority: 15, category: "financial" },
          { name: "Chart of Accounts", href: "/financial/accounts", icon: BookOpen, roles: ["principal"], priority: 16, category: "financial" },
          { name: "Periods", href: "/financial/periods", icon: CalendarDays, roles: ["principal"], priority: 17, category: "financial" },
          { name: "Banking", href: "/financial/banking", icon: Building, roles: ["principal"], priority: 18, category: "financial" },
          { name: "Grants", href: "/financial/grants", icon: Gift, roles: ["principal"], priority: 19, category: "financial" },
          { name: "Trial Balance", href: "/financial/trial-balance", icon: Scale, roles: ["principal"], priority: 20, category: "financial" },
          { name: "Account Statement", href: "/financial/account-statement", icon: BookOpen, roles: ["principal"], priority: 20, category: "financial" },
          { name: "Financial Statements", href: "/financial/financial-statements", icon: FileText, roles: ["principal"], priority: 20, category: "financial" },
          { name: "Management Reports", href: "/financial/statements", icon: TrendingUp, roles: ["principal"], priority: 20, category: "financial" },
        ],
      },
    ],
    secondary: [
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["principal"],
        priority: 7,
      },
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["principal"],
        priority: 9,
      },
    ],
    tertiary: [
    ],
  },

  // CFO - approves, and owns the chart of accounts
  cfo: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["cfo"],
        priority: 1,
      },
      {
        name: "My Approvals",
        href: "/my-approvals",
        icon: ClipboardList,
        roles: ["cfo"],
        priority: 2,
      },
      {
        // Salary changes are reviewed and approved here, so both halves of
        // the chain need a way in.
        name: "Staff",
        href: "/staff",
        icon: Users,
        roles: ["cfo"],
        priority: 3,
      },
      {
        name: "Financial",
        href: "/financial",
        icon: DollarSign,
        roles: ["cfo"],
        priority: 3,
        children: [
          { name: "Students", href: "/financial/students", icon: Users, roles: ["cfo"], priority: 1, category: "financial" },
          { name: "Fees", href: "/financial/fees", icon: DollarSign, roles: ["cfo"], priority: 2, category: "financial" },
          { name: "Fee Structures", href: "/financial/fee-structures", icon: ClipboardList, roles: ["cfo"], priority: 3, category: "financial" },
          { name: "Payments", href: "/financial/payments", icon: CreditCard, roles: ["cfo"], priority: 4, category: "financial" },
          { name: "Refunds", href: "/financial/refunds", icon: ArrowRightLeft, roles: ["cfo"], priority: 5, category: "financial" },
          { name: "Transport Fees", href: "/financial/transport", icon: Bus, roles: ["cfo"], priority: 6, category: "financial" },
          { name: "Boarding Fees", href: "/financial/boarding", icon: Home, roles: ["cfo"], priority: 7, category: "financial" },
          { name: "Journals", href: "/financial/journals", icon: BookOpen, roles: ["cfo"], priority: 8, category: "financial" },
          { name: "Payables", href: "/financial/payables", icon: Truck, roles: ["cfo"], priority: 9, category: "financial" },
          { name: "Budgets", href: "/financial/budgets", icon: FileText, roles: ["cfo"], priority: 10, category: "financial" },
          { name: "Payroll", href: "/financial/payroll", icon: Wallet, roles: ["cfo"], priority: 11, category: "financial" },
          { name: "Cafeteria Orders", href: "/financial/cafeteria", icon: Utensils, roles: ["cfo"], priority: 12, category: "financial" },
          { name: "Uniform Orders", href: "/financial/uniforms", icon: Shirt, roles: ["cfo"], priority: 13, category: "financial" },
          { name: "Invoices", href: "/financial/invoices", icon: FileText, roles: ["cfo"], priority: 14, category: "financial" },
          { name: "Credit Notes", href: "/financial/credit-notes", icon: FileText, roles: ["cfo"], priority: 15, category: "financial" },
          { name: "Chart of Accounts", href: "/financial/accounts", icon: BookOpen, roles: ["cfo"], priority: 16, category: "financial" },
          { name: "Periods", href: "/financial/periods", icon: CalendarDays, roles: ["cfo"], priority: 17, category: "financial" },
          { name: "Banking", href: "/financial/banking", icon: Building, roles: ["cfo"], priority: 18, category: "financial" },
          { name: "Grants", href: "/financial/grants", icon: Gift, roles: ["cfo"], priority: 19, category: "financial" },
          { name: "Trial Balance", href: "/financial/trial-balance", icon: Scale, roles: ["cfo"], priority: 20, category: "financial" },
          { name: "Account Statement", href: "/financial/account-statement", icon: BookOpen, roles: ["cfo"], priority: 20, category: "financial" },
          { name: "Financial Statements", href: "/financial/financial-statements", icon: FileText, roles: ["cfo"], priority: 20, category: "financial" },
          { name: "Management Reports", href: "/financial/statements", icon: TrendingUp, roles: ["cfo"], priority: 20, category: "financial" },
        ],
      },
      {
        // The CFO posts asset entries: capitalising, depreciation and disposal.
        name: "Fixed Assets",
        href: "/inventory",
        icon: Building,
        roles: ["cfo"],
        priority: 4,
      },
    ],
    secondary: [
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["cfo"],
        priority: 7,
      },
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["cfo"],
        priority: 9,
      },
    ],
    tertiary: [
    ],
  },

  // Librarian - Library management
  librarian: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["librarian"],
        priority: 1,
      },
      {
        name: "Librarian Dashboard",
        href: "/librarian/dashboard",
        icon: Library,
        roles: ["librarian"],
        priority: 2,
      },
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["librarian"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["librarian"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["librarian"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // School Doctor - Medical management
  school_doctor: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["school_doctor"],
        priority: 1,
      },
      {
        name: "School Doctor Dashboard",
        href: "/doctor/dashboard",
        icon: Stethoscope,
        roles: ["school_doctor"],
        priority: 2,
      },
      {
        name: "Medical",
        href: "/medical",
        icon: Heart,
        roles: ["school_doctor"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["school_doctor"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["school_doctor"],
        priority: 7,
      },
    ],
    tertiary: [
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["school_doctor"],
        priority: 13,
      },
    ],
  },

  // Transport Manager - Transport management
  transport_manager: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["transport_manager"],
        priority: 1,
      },
      {
        name: "Transport Manager Dashboard",
        href: "/transport-manager/dashboard",
        icon: Car,
        roles: ["transport_manager"],
        priority: 2,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["transport_manager"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["transport_manager"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["transport_manager"],
        priority: 7,
      },
    ],
    tertiary: [
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["transport_manager"],
        priority: 13,
      },
    ],
  },

  // Bus Driver - Transport operations
  bus_driver: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["bus_driver"],
        priority: 1,
      },
      {
        name: "Bus Driver Dashboard",
        href: "/bus-driver/dashboard",
        icon: Bus,
        roles: ["bus_driver"],
        priority: 2,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["bus_driver"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["bus_driver"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["bus_driver"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Hostel Matron - Hostel management
  hostel_matron: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["hostel_matron"],
        priority: 1,
      },
      {
        name: "Hostel Matron Dashboard",
        href: "/hostel-matron/dashboard",
        icon: Home,
        roles: ["hostel_matron"],
        priority: 2,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["hostel_matron"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["hostel_matron"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["hostel_matron"],
        priority: 7,
      },
    ],
    tertiary: [
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["hostel_matron"],
        priority: 13,
      },
    ],
  },

  // Hostel Custodian - Hostel operations
  hostel_custodian: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["hostel_custodian"],
        priority: 1,
      },
      {
        name: "Hostel Custodian Dashboard",
        href: "/hostel-custodian/dashboard",
        icon: Home,
        roles: ["hostel_custodian"],
        priority: 2,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["hostel_custodian"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["hostel_custodian"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["hostel_custodian"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Kitchen Manager - Kitchen operations
  kitchen_manager: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["kitchen_manager"],
        priority: 1,
      },
      {
        name: "Kitchen Manager Dashboard",
        href: "/kitchen-manager/dashboard",
        icon: ChefHat,
        roles: ["kitchen_manager"],
        priority: 2,
      },
      {
        name: "Cafeteria",
        href: "/cafeteria",
        icon: Utensils,
        roles: ["kitchen_manager"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["kitchen_manager"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["kitchen_manager"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Cafeteria Manager - Cafeteria operations
  cafeteria_manager: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["cafeteria_manager"],
        priority: 1,
      },
      {
        name: "Cafeteria Manager Dashboard",
        href: "/cafeteria-manager/dashboard",
        icon: Utensils,
        roles: ["cafeteria_manager"],
        priority: 2,
      },
      {
        name: "Cafeteria",
        href: "/cafeteria",
        icon: Utensils,
        roles: ["cafeteria_manager"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["cafeteria_manager"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["cafeteria_manager"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Sports Master - Sports management
  sports_master: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["sports_master"],
        priority: 1,
      },
      {
        name: "Sports Master Dashboard",
        href: "/sports-master/dashboard",
        icon: Dumbbell,
        roles: ["sports_master"],
        priority: 2,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["sports_master"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["sports_master"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["sports_master"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Uniform Manager - Uniform management
  uniform_manager: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["uniform_manager"],
        priority: 1,
      },
      {
        name: "Uniform Manager Dashboard",
        href: "/uniform-manager/dashboard",
        icon: Shirt,
        roles: ["uniform_manager"],
        priority: 2,
      },
      {
        name: "Uniforms",
        href: "/uniforms",
        icon: Shirt,
        roles: ["uniform_manager"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["uniform_manager"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["uniform_manager"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Inventory Manager - Inventory management
  inventory_manager: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["inventory_manager"],
        priority: 1,
      },
      {
        name: "Inventory Manager Dashboard",
        href: "/inventory-manager/dashboard",
        icon: Package,
        roles: ["inventory_manager"],
        priority: 2,
      },
      {
        name: "Inventory",
        href: "/inventory",
        icon: Building,
        roles: ["inventory_manager"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["inventory_manager"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["inventory_manager"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // School Counsellor - Counseling services
  school_counsellor: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["school_counsellor"],
        priority: 1,
      },
      {
        name: "School Counsellor Dashboard",
        href: "/counsellor/dashboard",
        icon: UserCog,
        roles: ["school_counsellor"],
        priority: 2,
      },
      {
        name: "Counseling",
        href: "/counseling",
        icon: MessageCircle,
        roles: ["school_counsellor"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["school_counsellor"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["school_counsellor"],
        priority: 7,
      },
    ],
    tertiary: [
    ],
  },

  // Disciplinary Committee - Discipline management
  disciplinary_committee: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["disciplinary_committee"],
        priority: 1,
      },
      {
        name: "Disciplinary Committee Dashboard",
        href: "/disciplinary-committee/dashboard",
        icon: Shield,
        roles: ["disciplinary_committee"],
        priority: 2,
      },
      {
        name: "Disciplinary",
        href: "/disciplinary",
        icon: Shield,
        roles: ["disciplinary_committee"],
        priority: 3,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["disciplinary_committee"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["disciplinary_committee"],
        priority: 7,
      },
    ],
    tertiary: [
      {
        name: "Emergency",
        href: "/emergency",
        icon: AlertTriangle,
        roles: ["disciplinary_committee"],
        priority: 14,
      },
    ],
  },

  // Parent - Parent portal
  parent: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["parent"],
        priority: 1,
      },
      {
        name: "Parent Portal",
        href: "/portal",
        icon: Users,
        roles: ["parent"],
        priority: 2,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["parent"],
        priority: 4,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["parent"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["parent"],
        priority: 6,
      },
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        roles: ["parent"],
        priority: 7,
      },
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["parent"],
        priority: 8,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["parent"],
        priority: 10,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["parent"],
        priority: 12,
      },
      {
        name: "Cafeteria",
        href: "/cafeteria",
        icon: Utensils,
        roles: ["parent"],
        priority: 11,
      },
    ],
    tertiary: [
      {
        name: "Scholarships",
        href: "/scholarships",
        icon: Gift,
        roles: ["parent"],
        priority: 12,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["parent"],
        priority: 13,
      },
      {
        name: "Uniforms",
        href: "/uniforms",
        icon: Shirt,
        roles: ["parent"],
        priority: 16,
      },
    ],
  },

  // Student - Student portal
  student: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["student"],
        priority: 1,
      },
      {
        name: "My Progress",
        href: "/student/gamification",
        icon: Award,
        roles: ["student"],
        priority: 2,
      },
      {
        name: "Results & Report",
        href: "/portal",
        icon: Users,
        roles: ["student"],
        priority: 3,
      },
      {
        name: "Library",
        href: "/library",
        icon: Library,
        roles: ["student"],
        priority: 4,
      },
      {
        name: "Events",
        href: "/events",
        icon: CalendarDays,
        roles: ["student"],
        priority: 5,
      },
    ],
    secondary: [
      {
        name: "Timetable",
        href: "/timetable",
        icon: Clock,
        roles: ["student"],
        priority: 6,
      },
      {
        name: "Sports",
        href: "/sports",
        icon: Award,
        roles: ["student"],
        priority: 7,
      },
      {
        name: "Communication",
        href: "/communication",
        icon: MessageSquare,
        roles: ["student"],
        priority: 8,
      },
      {
        name: "Transport",
        href: "/transport",
        icon: Bus,
        roles: ["student"],
        priority: 10,
      },
      {
        name: "Hostel",
        href: "/hostel",
        icon: Home,
        roles: ["student"],
        priority: 12,
      },
      {
        name: "Cafeteria",
        href: "/cafeteria",
        icon: Utensils,
        roles: ["student"],
        priority: 11,
      },
    ],
    tertiary: [
      {
        name: "Uniforms",
        href: "/uniforms",
        icon: Shirt,
        roles: ["student"],
        priority: 13,
      },
    ],
  },

  board_viewer: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["board_viewer"],
        priority: 1,
      },
      {
        name: "Governance Oversight",
        href: "/governance",
        icon: BarChart3,
        roles: ["board_viewer"],
        priority: 2,
      },
    ],
    secondary: [],
    tertiary: [],
  },

  donor_viewer: {
    primary: [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        roles: ["donor_viewer"],
        priority: 1,
      },
      {
        name: "Governance Oversight",
        href: "/governance",
        icon: BarChart3,
        roles: ["donor_viewer"],
        priority: 2,
      },
    ],
    secondary: [],
    tertiary: [],
  },
};

// Helper function to get role-specific navigation
export const getRoleBasedNavigation = (userRole: string): NavigationItem[] => {
  const roleConfig = roleBasedNavigation[userRole];

  // A role without an entry of its own falls back to the shared list, and then
  // goes through the same additions below. Returning early here was how a head
  // of department -- who has no entry, but is named in every one of those
  // role lists -- ended up with no Appraisals link while being the person who
  // conducts the appraisals.
  const allItems: NavigationItem[] = roleConfig
    ? [...roleConfig.primary, ...roleConfig.secondary, ...roleConfig.tertiary]
    : navigationItems.filter(
        (item) => item.roles.includes("*") || item.roles.includes(userRole),
      );

  const academicRoles = [
    "admin",
    "director",
    "head_teacher",
    "director_of_studies",
    "head_of_department",
    "class_teacher",
    "teacher",
  ];
  const reportRoles = [...academicRoles, "principal", "cfo", "bursar"];
  if (
    academicRoles.includes(userRole) &&
    !allItems.some((item) => item.href === "/syllabi")
  ) {
    allItems.push({
      name: "Syllabi",
      href: "/syllabi",
      icon: FileText,
      roles: academicRoles,
      priority: 8,
    });
  }
  if (
    reportRoles.includes(userRole) &&
    !allItems.some((item) => item.href === "/reports")
  ) {
    allItems.push({
      name: "Reports",
      href: "/reports",
      icon: BarChart3,
      roles: reportRoles,
      priority: 9,
    });
  }
  const classSetupRoles = [
    "admin",
    "director",
    "head_teacher",
    "director_of_studies",
  ];
  if (
    classSetupRoles.includes(userRole) &&
    !allItems.some((item) => item.href === "/class-setup")
  ) {
    allItems.push({
      name: "Classes & Streams",
      href: "/class-setup",
      icon: Users,
      roles: classSetupRoles,
      priority: 7,
    });
  }
  const parentManageRoles = ["admin", "director", "head_teacher"];
  // Every role on the school's payroll. Parents, students and the external
  // governance viewers are not appraised and do not see this.
  const appraisalRoles = [
    "admin",
    "director",
    "principal",
    "head_teacher",
    "director_of_studies",
    "head_of_department",
    "class_teacher",
    "teacher",
    "school_doctor",
    "hostel_matron",
    "hostel_custodian",
    "kitchen_manager",
    "cafeteria_manager",
    "sports_master",
    "uniform_manager",
    "inventory_manager",
    "school_counsellor",
    "librarian",
    "cfo",
    "bursar",
    "transport_manager",
    "bus_driver",
    "disciplinary_committee",
  ];
  if (
    classSetupRoles.includes(userRole) &&
    !allItems.some((item) => item.href === "/student-lifecycle")
  ) {
    allItems.push({
      name: "Student Lifecycle",
      href: "/student-lifecycle",
      icon: ArrowRightLeft,
      roles: classSetupRoles,
      priority: 8,
    });
  }
  if (
    parentManageRoles.includes(userRole) &&
    !allItems.some((item) => item.href === "/parents")
  ) {
    allItems.push({
      name: "Parents & Guardians",
      href: "/parents",
      icon: Users2,
      priority: 6,
      roles: parentManageRoles,
    });
  }
  // Appraisals are shown to everyone the school employs, because everyone has
  // one -- a teacher sees their own, a head of department sees the ones they
  // conduct, and HR sees the register. Which appraisals a person can actually
  // open is decided server-side, not by this list.
  if (
    appraisalRoles.includes(userRole) &&
    !allItems.some((item) => item.href === "/appraisals")
  ) {
    allItems.push({
      name: "Appraisals",
      href: "/appraisals",
      icon: ClipboardCheck,
      roles: appraisalRoles,
      priority: 10,
    });
  }

  return allItems.sort((a, b) => (a.priority || 999) - (b.priority || 999));
};

// Helper function to get navigation categories for a role
export const getNavigationCategories = (userRole: string) => {
  const roleConfig = roleBasedNavigation[userRole];
  if (!roleConfig) {
    const items = getRoleBasedNavigation(userRole);
    return {
      primary: items.filter((item) => (item.priority || 999) <= 5),
      secondary: items.filter(
        (item) => (item.priority || 999) > 5 && (item.priority || 999) <= 10,
      ),
      tertiary: items.filter((item) => (item.priority || 999) > 10),
      quickActions: [],
    };
  }

  const academicRoles = [
    "admin",
    "director",
    "head_teacher",
    "director_of_studies",
    "head_of_department",
    "class_teacher",
    "teacher",
  ];
  const reportRoles = [...academicRoles, "principal", "cfo", "bursar"];
  const secondary = [...roleConfig.secondary];
  if (
    academicRoles.includes(userRole) &&
    !secondary.some((item) => item.href === "/syllabi")
  ) {
    secondary.push({
      name: "Syllabi",
      href: "/syllabi",
      icon: FileText,
      roles: academicRoles,
      priority: 8,
    });
  }
  if (
    reportRoles.includes(userRole) &&
    !secondary.some((item) => item.href === "/reports")
  ) {
    secondary.push({
      name: "Reports",
      href: "/reports",
      icon: BarChart3,
      roles: reportRoles,
      priority: 9,
    });
  }
  const classSetupRoles = [
    "admin",
    "director",
    "head_teacher",
    "director_of_studies",
  ];
  if (
    classSetupRoles.includes(userRole) &&
    !secondary.some((item) => item.href === "/class-setup")
  ) {
    secondary.push({
      name: "Classes & Streams",
      href: "/class-setup",
      icon: Users,
      roles: classSetupRoles,
      priority: 7,
    });
  }
  const parentManageRoles = ["admin", "director", "head_teacher"];
  if (
    parentManageRoles.includes(userRole) &&
    !secondary.some((item) => item.href === "/parents")
  ) {
    secondary.push({
      name: "Parents & Guardians",
      href: "/parents",
      icon: Users2,
      roles: parentManageRoles,
      priority: 6,
    });
  }

  return {
    primary: roleConfig.primary,
    secondary,
    tertiary: roleConfig.tertiary,
    quickActions: roleConfig.quickActions || [],
  };
};
